import type { PaaOldSheet } from "../api";
import type { SetPage } from "../combine/types";
import {
  formatRevision,
  pageIdsFor,
  parseRevisionMinor,
  type PaaRow,
} from "../types";

// What each updated page supersedes — the decision everything else rests on.
//
// WHY IT IS ITS OWN MODULE. The printed label cannot be trusted: a sheet marked
// 0.01 does not always supersede the same-numbered approved sheet, and a page
// that looks new can be a renumbered replacement. Getting it wrong costs twice —
// the comparison describes the wrong pair, and the combined set keeps the old
// page or holds both versions.
//
// WHERE IT IS ASKED. On the row, and on the drawing surface, which are the same
// decision in two densities. It briefly had a step of its own in front of
// review; that step was retired because it made a pass through every page
// compulsory before anything could be described, and the list is the home. The
// logic stayed, because it was never about the screen.
//
// The decision is written to `compareWith`, which already exists and already
// flows: the comparison diffs against it, and PaaCombinePlanner treats it as its
// highest-priority placement signal. Nothing new is being invented here — it is
// being asked where it can be answered.
//
// ONE CONFIRMATION, not two. `reviewed` covers the whole row: what it supersedes
// and what the note says about it. A separate tick for the pairing alone was a
// second thing to clear on every sheet of a ninety-page filing, and two
// confirmations that can disagree are two confirmations nobody trusts.

/** An approved-set page, as either source of the index describes it.
 *
 *  There are two, and they are the same read: the comparison's `context` event
 *  (`PaaOldSheet`) and the combine plan's approved index (`SetPage`). Whichever
 *  is in hand answers this question, so both map to one shape rather than every
 *  caller branching. */
export interface ApprovedSheet {
  page: number;
  sheetId: string;
}

export function approvedFromCompare(
  sheets: readonly PaaOldSheet[] | null | undefined,
): ApprovedSheet[] {
  return (sheets ?? []).map((s) => ({ page: s.page, sheetId: s.sheet }));
}

export function approvedFromCombine(
  sheets: readonly SetPage[] | null | undefined,
): ApprovedSheet[] {
  return (sheets ?? []).map((s) => ({ page: s.page, sheetId: s.sheetId }));
}

/** How a pairing was arrived at. Shown beside it: a decision nobody can
 *  question is one everybody rubber-stamps. */
export type PairingBasis =
  /** She said so. */
  | "operator"
  /** Same sheet number in the approved set. */
  | "sheetNumber"
  /** Nothing in the approved set to pair with. */
  | "none";

export interface Pairing {
  row: PaaRow;
  /** The approved page this row relates to, 1-based, or null. */
  approvedPage: number | null;
  approvedSheetId: string | null;
  basis: PairingBasis;
  /** A superseding sheet with nothing to supersede. Not an error — it is
   *  usually a renumbered sheet, which is the whole reason she is looking — but
   *  it cannot be left as "S" with no baseline, because the combined set would
   *  then hold no replacement for anything. */
  unmatched: boolean;
  /**
   * A same-number collision she has not settled: this page is marked new, and a
   * sheet of that number is already in the approved set.
   *
   * The mirror of `unmatched` and by far the more dangerous of the two. The
   * action is inferred from the printed revision, so a sheet reissued at 0.00
   * reads as new — and left alone the combined set carries BOTH versions of the
   * drawing. The planner has always warned about it and then done it anyway,
   * on a row buried at the end of a ninety-page list.
   *
   * There is no default. It stays true until she picks one of the three
   * outcomes, and it never blocks the build — a prompt she cannot dismiss is a
   * prompt she learns to click through.
   */
  newButPresent: boolean;
  /** She has settled it, and how. Undefined while it is still a question. */
  sameNumberChoice: PaaRow["sameNumberChoice"];
  /** The approved sheet of the same number, when one exists — whether or not it
   *  is the active baseline. What `newButPresent` is talking about. */
  sameNumberPage: number | null;
}

/** Rows worth pairing. A page with no sheet number cannot be matched against
 *  anything until she names it. */
export function isPairable(row: PaaRow): boolean {
  return row.sheetId.trim().length > 0;
}

const same = (a: string | null | undefined, b: string | null | undefined) =>
  (a ?? "").trim().toUpperCase() === (b ?? "").trim().toUpperCase();

function findApproved(
  sheets: readonly ApprovedSheet[],
  sheetId: string | null | undefined,
) {
  if (!sheetId?.trim()) return null;
  return sheets.find((s) => same(s.sheetId, sheetId)) ?? null;
}

/**
 * The pairing for one row.
 *
 * An "O" row is the case that reads backwards: an omitted sheet names an
 * APPROVED sheet leaving the set, so its own id is the thing to look up. "N" and
 * "S" name a page in the updated set, and their pairing is what it displaces.
 */
export function pairingFor(
  row: PaaRow,
  approvedSheets: readonly ApprovedSheet[],
): Pairing {
  const chosen = row.compareWith?.trim();
  const sameNumber = findApproved(approvedSheets, row.sheetId);
  const chosenHit = chosen ? findApproved(approvedSheets, chosen) : null;

  // An "N" sheet has NOTHING to supersede unless she has said otherwise, and
  // saying otherwise is the renumbered / broken-out case the feature exists for.
  //
  // This used to fall through to a match on sheet number, which put an approved
  // drawing beside a page labelled New and captioned it "matched by sheet
  // number" — a baseline nothing was going to use, and a screen contradicting
  // itself. What the coincidence actually means is reported as `newButPresent`.
  const isNew = row.action === "N" && !chosen;
  const active = chosen ? chosenHit : isNew ? null : sameNumber;

  return {
    row,
    approvedPage: active?.page ?? null,
    approvedSheetId: active?.sheetId ?? null,
    basis: chosen ? "operator" : active ? "sheetNumber" : "none",
    unmatched: !isNew && !active,
    // Settled once she has chosen, whichever way — the prompt is about the
    // decision being unmade, not about the collision existing.
    newButPresent: isNew && !!sameNumber && !row.sameNumberChoice,
    sameNumberChoice: row.sameNumberChoice,
    sameNumberPage: sameNumber?.page ?? null,
  };
}

export function pairings(
  rows: readonly PaaRow[],
  approvedSheets: readonly ApprovedSheet[],
): Pairing[] {
  return rows.filter(isPairable).map((r) => pairingFor(r, approvedSheets));
}

export interface PairingProgress {
  total: number;
  /** Rows that cannot be left as they are: superseding with nothing to
   *  supersede. Counted for the operator's benefit, never to block her. */
  unmatched: number;
  /** Rows marked new whose number is already in the approved set. Counted
   *  separately from `unmatched` because it is the opposite mistake and has the
   *  opposite fix — one number covering both would move whenever either did. */
  newButPresent: number;
}

export function pairingProgress(
  rows: readonly PaaRow[],
  approvedSheets: readonly ApprovedSheet[],
): PairingProgress {
  const all = pairings(rows, approvedSheets);
  return {
    total: all.length,
    unmatched: all.filter((p) => p.unmatched).length,
    newButPresent: all.filter((p) => p.newButPresent).length,
  };
}

/** Point a row at a different approved sheet, or back to automatic.
 *
 *  Writes `compareWith`, and drops the row's confirmation: the note was written
 *  against the old pairing, so it is no longer about this pair. */
export function setPairing(
  rows: readonly PaaRow[],
  id: string,
  approvedSheetId: string | null,
): PaaRow[] {
  return rows.map((r) =>
    r.id === id
      ? {
          ...r,
          compareWith: approvedSheetId?.trim() ? approvedSheetId : null,
          reviewed: false,
        }
      : r,
  );
}

/**
 * Turn one row into a superseding sheet, completely.
 *
 * WHAT WENT WRONG WITHOUT THIS. Settling a collision wrote `action: "S"` and
 * nothing else, so the AI1 page ids were never re-derived and Section 2 printed
 * "Z-002.00 →" with nothing after the arrow. Caught by looking at the screen;
 * no test failed, because every test asserted the action.
 *
 * And re-deriving alone is not enough: an "S" row's two page ids are the
 * revision before and the revision itself, so a sheet still reading 0.00 gives
 * the SAME id twice. The bump is part of the answer, not a separate chore —
 * the ordinary cause of this whole collision is an architect who reissued a
 * sheet without touching its revision, which is the case `revisionLooksStale`
 * already names and already offers `revision + 1` for. Saying "it supersedes
 * it" is saying the drawing should have carried the next revision up.
 *
 * A revision that already moved is left alone: it is the operator's, and only
 * a 0.00 cannot produce a superseding id.
 */
function asSupersede(row: PaaRow): PaaRow {
  const minor = parseRevisionMinor(row.revision);
  const revision = minor <= 0 ? formatRevision(minor + 1) : row.revision;
  return {
    ...row,
    action: "S",
    actionLocked: true,
    revision,
    ...pageIdsFor(row.sheetId, revision, "S"),
    reviewed: false,
  };
}

/**
 * Settle a same-number collision.
 *
 * "updated" makes it a supersede, which is the ordinary answer and the one that
 * leaves a single version of the drawing in the filing. "both" leaves the action
 * as new and records that somebody meant it. Keeping the approved sheet instead
 * is not expressed here: that page is not part of the filing, so the caller
 * removes the row.
 *
 * Either way the confirmation goes, because what she confirmed just changed
 * underneath it.
 */
export function settleSameNumber(
  rows: readonly PaaRow[],
  id: string,
  choice: "updated" | "both",
): PaaRow[] {
  return rows.map((r) =>
    r.id !== id
      ? r
      : choice === "updated"
        ? { ...asSupersede(r), sameNumberChoice: choice }
        : { ...r, sameNumberChoice: choice, action: "N", actionLocked: true, reviewed: false },
  );
}

/**
 * Settle every outstanding collision the same way.
 *
 * Not a convenience — a correction of scale. On 1259 St Marks, 13 of 24 sheets
 * print 0.00 and every one of them is already in the approved set: the
 * architect reissued the set without bumping the revisions. That is ONE fact
 * about the filing, and asking it thirteen times turns a real question into a
 * wall nobody reads. Answering it thirteen times individually is still there,
 * and every row still shows which way it went with an undo beside it.
 *
 * Only "updated" is offered in bulk. Filing both versions is rare and per-sheet
 * by nature, and "keep the approved one" removes rows — neither is something to
 * do to a whole filing on one click.
 */
export function settleAllSameNumber(
  rows: readonly PaaRow[],
  approvedSheets: readonly ApprovedSheet[],
): PaaRow[] {
  const outstanding = new Set(
    pairings(rows, approvedSheets)
      .filter((p) => p.newButPresent)
      .map((p) => p.row.id),
  );
  if (outstanding.size === 0) return rows as PaaRow[];
  // Through the same helper as the per-row answer. Two spellings of "this page
  // supersedes that one" is the trap this codebase keeps writing down.
  return rows.map((r) =>
    outstanding.has(r.id)
      ? { ...asSupersede(r), sameNumberChoice: "updated" as const }
      : r,
  );
}

/**
 * Re-open the question — the undo behind the settled line.
 *
 * Gives back the 0.00 the bump above took, which is exact for the only bump
 * this module ever makes (0.00 is the sole revision `asSupersede` rewrites, and
 * it always rewrites it to 0.01). The page ids come back with it, or an
 * un-settled row would keep printing the superseding pair it no longer is.
 */
export function unsettleSameNumber(rows: readonly PaaRow[], id: string): PaaRow[] {
  return rows.map((r) => {
    if (r.id !== id) return r;
    const revision =
      r.sameNumberChoice === "updated" && parseRevisionMinor(r.revision) === 1
        ? formatRevision(0)
        : r.revision;
    return {
      ...r,
      sameNumberChoice: undefined,
      action: "N",
      revision,
      ...pageIdsFor(r.sheetId, revision, "N"),
      reviewed: false,
    };
  });
}
